import { useEffect, useMemo, useState } from 'react';
import VideoViewsOptions from './Layouts/LayoutOptions';
import { React } from 'Imports';
import { FileUploadTypeEnum, VideoAssetResponse, VideoEventWorkflowStatusIdEnum } from '$Generated/api';
import { VideoAsset, VideoViewType } from '$Utilities/models/videoViews';
import VideoPlayerSyncProvider from '$State/VideoPlayerSync/VideoPlayerSyncProvider';
import VideoViewsLayout from './Layouts/VideoViewsLayout';
import { Grid } from 'MaterialUIComponents';
import { useAiOverlayContext } from '$State/AiOverlay/AiOverlayContext';
import AiOverlayLoadingOverlay from './AiOverlayLoadingOverlay';
import DropdownSwitch from '$Components/FilterComponents/Filters/DropdownSwitch';
import { isEncompass } from '$Utilities/integrationPlatform';

interface VideoViewsProps {
    videoAssetsRaw: VideoAssetResponse[];
    videoEventId: number;
    videoEventDisplayId: string;
    videoStatus?: VideoEventWorkflowStatusIdEnum;
    isVideoRecall?: boolean;
    onVideosStarted: () => void;
    onVideosWatched: () => void;
    onVideoDownloaded: () => void;
}

const styles = require('./VideoViews.scss') as {
    toolbar: string;
    aiOverlayToggleSection: string;
    aiOverlayToggleTextEncompass: string;
    videoAreaWrapper: string;
};

// Virtual channel numbers for old-format events
// Preserves the previous FrontVideo-as-primary ordering through the same channel-based mechanism.
const legacyTypeToChannel: Partial<Record<FileUploadTypeEnum, number>> = {
    FrontVideo: 1,
    RearVideo: 2,
    SideVideo: 3,
    AltVideo: 4,
};

const VideoViews = (props: VideoViewsProps) => {
    const [activeViewOption, setActiveViewOption] = useState<VideoViewType>('Single');
    const [activeFullScreen, setActiveFullScreen] = useState<boolean>(false);
    const { aiOverlayOn, aiOverlayLoading, showAiOverlayToggle, toggleAiOverlay } = useAiOverlayContext();

    const { videoAssets, numberOfChannels, primaryChannelNumber } = useMemo(() => {
        if (!props.videoAssetsRaw) {
            return {
                videoAssets: [] as VideoAsset[],
                numberOfChannels: 0,
                primaryChannelNumber: 1,
            };
        }

        // New events have channelNumber populated; old events do not.
        const isChannelBased = props.videoAssetsRaw.some((a) => a.channelNumber != null);

        // New events: primary comes from the thumbnail's channelNumber.
        // Old events: primary is determined by the legacy type-to-channel mapping, using the Thumbnail's fileUploadTypeId.
        const primaryChannelNumber = isChannelBased
            ? (props.videoAssetsRaw.find((a) => a.type === 'Thumbnail')?.channelNumber ?? 1)
            : (legacyTypeToChannel[props.videoAssetsRaw.find((a) => a.type === 'Thumbnail')?.fileUploadTypeId ?? 'FrontVideo'] ?? 1);

        const assets: VideoAsset[] = [];

        props.videoAssetsRaw
            .filter((asset) => asset.type === 'Transcode')
            .forEach((asset) => {
                let videoUrl = '';
                if (asset.videoAssetStorageId && asset.videoAssetStorageId === 'GcpFs') {
                    const storageDetails = JSON.parse(asset.storageDetailsJson || '');
                    videoUrl = storageDetails.signedURL;
                }
                const channelNumber = isChannelBased ? asset.channelNumber : legacyTypeToChannel[asset.fileUploadTypeId!];
                assets.push({ type: asset.fileUploadTypeId!, url: videoUrl, aiUrl: asset.aiVideoUrl, channelNumber });
            });

        return {
            videoAssets: assets,
            numberOfChannels: assets.length,
            primaryChannelNumber,
        };
    }, [props.videoAssetsRaw]);

    useEffect(() => {
        if (numberOfChannels === 2) {
            setActiveViewOption('Double');
        } else if (numberOfChannels >= 4) {
            setActiveViewOption('Grid');
        } else if (numberOfChannels > 0) {
            setActiveViewOption('Single');
        }
    }, [numberOfChannels]);

    const handleOnViewChange = (type: VideoViewType) => {
        if (type === ('Full Screen' as VideoViewType)) {
            setActiveFullScreen(true);
        } else {
            if (activeFullScreen) {
                setActiveFullScreen(false);
            }
            setActiveViewOption(type);
        }
    };

    const handleOnVideosStarted = () => {
        props.onVideosStarted();
    };

    const handleOnVideosWatched = () => {
        props.onVideosWatched();
    };

    return (
        videoAssets &&
        videoAssets.length > 0 && (
            <Grid container className="video-views-container">
                <Grid item xs={12}>
                    <div className={styles.toolbar}>
                        <VideoViewsOptions
                            activeViewOption={activeViewOption}
                            numberOfChannels={numberOfChannels}
                            onViewChange={handleOnViewChange}
                            activeFullScreen={activeFullScreen}
                        />

                        {showAiOverlayToggle && (
                            <div className={styles.aiOverlayToggleSection}>
                                <DropdownSwitch
                                    checked={aiOverlayOn}
                                    onChange={toggleAiOverlay}
                                    toggleText="AI Overlay"
                                    disabled={aiOverlayLoading}
                                    textClassName={isEncompass ? styles.aiOverlayToggleTextEncompass : undefined}
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles.videoAreaWrapper}>
                        {aiOverlayLoading && <AiOverlayLoadingOverlay />}
                        <VideoPlayerSyncProvider
                            key={props.videoEventId}
                            videoEventId={props.videoEventId}
                            videoEventDisplayId={props.videoEventDisplayId}
                            isFullView={false}
                        >
                            <VideoViewsLayout
                                activeViewType={activeViewOption}
                                numberOfChannels={numberOfChannels}
                                primaryChannelNumber={primaryChannelNumber}
                                videoAssets={videoAssets}
                                onVideosStarted={handleOnVideosStarted}
                                onVideosWatched={handleOnVideosWatched}
                                onVideoDownloaded={props.onVideoDownloaded}
                                activeFullScreen={activeFullScreen}
                                onCloseFullScreen={() => setActiveFullScreen(false)}
                            />
                        </VideoPlayerSyncProvider>
                    </div>
                </Grid>
            </Grid>
        )
    );
};

export default VideoViews;
